import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { motion, useDragControls, useMotionValue, animate } from 'framer-motion';
import { Clock, Loader2, AlertTriangle, Star } from 'lucide-react';

const DraggableEvent = ({
    event,
    top,
    height,
    left,
    width,
    isExpanded,
    formatTime,
    onEventClick,
    onEventVerticalDrop,
    onEventHorizontalDrop,
    onEventDrop2D,
    getHorizontalSnapDx,
    HOUR_HEIGHT,
    stepMinutes = 30,
    dragAxis = 'y',
}: any) => {
    const controls = useDragControls();
    const [isDragging, setIsDragging] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const longPressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const startPos = useRef({ x: 0, y: 0 });
    const boxRef = useRef<HTMLDivElement>(null);
    const [boxWidth, setBoxWidth] = useState<number | null>(null);

    const dragX = useMotionValue(0);
    const dragY = useMotionValue(0);

    const eventColorClass = event.color;
    // Layout compatto (una riga) solo per box VISIVAMENTE bassi: si basa sull'altezza
    // renderizzata, non sulla durata reale — così un servizio breve che riempie lo slot
    // (slot-fill) usa lo stesso layout verticale degli altri appuntamenti.
    // La soglia sta sotto l'altezza di una riga (44px), che deve restare a due righe.
    const isMicro = height < 34;

    // Badge in alto a destra, nell'ordine del mockup: stellina, segnalazione, sconto.
    // Lo sconto mostra l'importo, non solo l'icona: è l'informazione che serve al volo.
    const adjustment = Number(event.priceAdjustment) || 0;

    // Larghezza reale del box: nelle colonne strette (più dipendenti, appuntamenti
    // sovrapposti) i badge mangiano tutto lo spazio del testo, quindi spariscono.
    useLayoutEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const measure = () => setBoxWidth(el.getBoundingClientRect().width);
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const badgeCount = (event.noShowOverlaps?.length > 0 ? 1 : 0)
        + (event.isFirstBooking ? 1 : 0)
        + (event.isReported ? 1 : 0);
    // Stima: ~15px per icona (11px + gap), ~40px per il chip dello sconto
    const badgesWidth = badgeCount * 15 + (adjustment !== 0 ? 40 : 0);
    // Sotto questa soglia il nome del servizio non si legge più: meglio niente icone
    const MIN_TEXT_WIDTH = 60;
    // 16px = padding orizzontale del box (px-2 su entrambi i lati)
    const contentWidth = boxWidth === null ? null : boxWidth - 16;
    const showBadges = badgesWidth > 0 && (contentWidth === null || contentWidth - badgesWidth >= MIN_TEXT_WIDTH);
    // Spazio riservato ai badge: solo quello che serve davvero, e solo se sono visibili
    const textStyle = showBadges ? { paddingRight: `${badgesWidth + 6}px` } : undefined;

    const badges = showBadges && (
        <div className="absolute top-1 right-1 z-20 flex items-center gap-1 pointer-events-none">
            {/* Pallino no-show: un no-show che si sovrappone a questo appuntamento non sdoppia
                la grafica, ma appare come un pallino grigio che apre il dettaglio del no-show. */}
            {event.noShowOverlaps?.length > 0 && (
                <button
                    type="button"
                    title="No-show in questo orario"
                    onClick={(e) => { e.stopPropagation(); onEventClick(event.noShowOverlaps[0]); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="w-3.5 h-3.5 rounded-full bg-gray-400 hover:bg-gray-500 ring-2 ring-white pointer-events-auto"
                />
            )}
            {event.isFirstBooking && <Star size={11} className="text-amber-500 fill-amber-400 flex-shrink-0" />}
            {event.isReported && <AlertTriangle size={11} className="text-orange-500 fill-orange-50 flex-shrink-0" />}
            {adjustment !== 0 && (
                <span
                    className={`px-1.5 py-px rounded-[3px] text-[10px] font-semibold leading-none tabular-nums ${
                        adjustment < 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                    }`}
                >
                    {adjustment < 0 ? '-' : '+'}€{Math.abs(adjustment)}
                </span>
            )}
        </div>
    );

    useEffect(() => {
        if (isDragging) {
            const handleTouchMove = (e: TouchEvent) => { e.preventDefault(); };
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.body.style.touchAction = 'none';
            document.body.style.overflow = 'hidden';
            return () => {
                document.removeEventListener('touchmove', handleTouchMove);
                document.body.style.touchAction = '';
                document.body.style.overflow = '';
            };
        }
    }, [isDragging]);

    // Reset transform whenever event's base position changes (after successful drop)
    useEffect(() => {
        dragX.set(0);
        dragY.set(0);
    }, [left, top]);

    const clearLongPress = () => {
        if (longPressTimeoutRef.current) {
            clearTimeout(longPressTimeoutRef.current);
            longPressTimeoutRef.current = null;
        }
    };

    const snapBack = () => {
        animate(dragX, 0, { type: 'spring', stiffness: 500, damping: 40 });
        animate(dragY, 0, { type: 'spring', stiffness: 500, damping: 40 });
    };

    const resetInstant = () => {
        dragX.set(0);
        dragY.set(0);
    };

    const handlePointerDown = (e: React.PointerEvent) => {
        // Track position for all events so pointup fires correctly on mobile
        startPos.current = { x: e.clientX, y: e.clientY };

        if (event.isGroup) return;
        if (e.button !== 0 && e.pointerType === 'mouse') return;

        longPressTimeoutRef.current = setTimeout(() => {
            setIsDragging(true);
            controls.start(e);
            if (window.navigator?.vibrate) window.navigator.vibrate(40);
        }, 300);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isDragging && longPressTimeoutRef.current) {
            const dx = e.clientX - startPos.current.x;
            const dy = e.clientY - startPos.current.y;
            if (Math.sqrt(dx * dx + dy * dy) > 10) clearLongPress();
        }
    };

    const handlePointerUp = (e: React.PointerEvent) => {
        clearLongPress();
        if (!isDragging) {
            if (event.isGroup) {
                // For grouped events, trigger click so CalendarGrid can navigate
                onEventClick(null);
            } else if (event.booking) {
                onEventClick(event.booking);
            }
        }
        setIsDragging(false);
    };

    const handleDragEnd = async (e: any, info: any) => {
        setIsDragging(false);
        clearLongPress();

        const absX = Math.abs(info.offset.x);
        const absY = Math.abs(info.offset.y);
        const xMoved = absX > 20;
        const yMoved = absY > 5;

        // Compute snapped new hour (null if no meaningful change)
        const computeNewHour = (): number | null => {
            const stepHours = stepMinutes / 60;
            const raw = event.startHour + info.offset.y / HOUR_HEIGHT;
            const snapped = Math.round(raw / stepHours) * stepHours;
            return Math.abs(snapped - event.startHour) > 0.01 ? snapped : null;
        };

        // Immediately snap transform to target, then await handler (optimistic UI)
        const snapToTarget = (snapX: number, snapY: number) => {
            dragX.set(snapX);
            dragY.set(snapY);
        };

        const tryDo = async (snapX: number, snapY: number, fn: () => Promise<void>) => {
            snapToTarget(snapX, snapY);
            setIsLoading(true);
            try { await fn(); resetInstant(); }
            catch { snapBack(); }
            finally { setIsLoading(false); }
        };

        if (dragAxis === 'xy') {
            if (!xMoved && !yMoved) { resetInstant(); return; }

            if (xMoved && !yMoved) {
                // Horizontal only
                const snapX = getHorizontalSnapDx ? getHorizontalSnapDx(event.booking, info.point.x) : info.offset.x;
                await tryDo(snapX, 0, () => onEventHorizontalDrop!(event.booking, info.point.x));
            } else if (!xMoved && yMoved) {
                // Vertical only
                const newHour = computeNewHour();
                if (newHour !== null) {
                    const snapY = (newHour - event.startHour) * HOUR_HEIGHT;
                    await tryDo(0, snapY, () => onEventVerticalDrop!(event.booking, newHour));
                } else resetInstant();
            } else {
                // Both axes: combined
                const newHour = computeNewHour();
                const snapX = getHorizontalSnapDx ? getHorizontalSnapDx(event.booking, info.point.x) : info.offset.x;
                const snapY = newHour !== null ? (newHour - event.startHour) * HOUR_HEIGHT : 0;
                await tryDo(snapX, snapY, () => onEventDrop2D!(event.booking, newHour, info.point.x));
            }
            return;
        }

        // Y-only mode
        if (!yMoved) { resetInstant(); return; }
        const newHour = computeNewHour();
        if (newHour !== null && onEventVerticalDrop) {
            const snapY = (newHour - event.startHour) * HOUR_HEIGHT;
            await tryDo(0, snapY, () => onEventVerticalDrop(event.booking, newHour));
        } else resetInstant();
    };

    return (
        <motion.div
            ref={boxRef}
            drag={!event.isGroup ? (dragAxis === 'xy' ? true : 'y') : false}
            dragControls={controls}
            dragListener={false}
            dragMomentum={false}
            style={{
                x: dragX,
                y: dragY,
                top: `${top}px`,
                height: `${height}px`,
                left: `${left}%`,
                width: `${width}%`,
                position: 'absolute',
                willChange: isDragging ? 'transform' : 'auto',
            }}
            onDragEnd={handleDragEnd}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={() => { clearLongPress(); setIsDragging(false); snapBack(); }}
            onContextMenu={(e) => e.preventDefault()}
            className={`z-10 transition-opacity transition-transform duration-150 ease-out pointer-events-auto ${isDragging
                ? 'z-50 cursor-grabbing opacity-60 scale-[1.02] shadow-xl'
                : 'cursor-pointer opacity-100 scale-100'
                }`}
        >
            <div
                className={`
                    relative w-full rounded shadow-sm overflow-hidden hover:brightness-95 transition-all group hover:shadow-md border-l-4 ${eventColorClass}
                    ${isMicro && !event.isGroup ? 'flex flex-row items-center px-2 border-l-4 h-full' : 'px-2 py-1 flex flex-col justify-start h-full'}
                `}
                style={{ minHeight: isMicro && !event.isGroup ? '28px' : 'auto' }}
            >
                {badges}
                {event.isGroup ? (
                    <>
                        <div className="flex justify-between items-start w-full">
                            <span className="text-xs font-bold leading-snug line-clamp-2">{event.title}</span>
                        </div>
                        <p className="text-[10px] opacity-80 font-medium leading-normal pb-0.5 line-clamp-1">{event.client}</p>
                    </>
                ) : isMicro ? (
                    <div className="flex items-center w-full gap-2 text-[11px] leading-tight whitespace-nowrap overflow-hidden pointer-events-none" style={textStyle}>
                        <span className="font-semibold truncate">{event.title}</span>
                        <span className="opacity-70 truncate hidden sm:inline">{event.client}</span>
                    </div>
                ) : (
                    <>
                        <p className="text-[11px] font-semibold leading-tight line-clamp-1 pointer-events-none" style={textStyle}>{event.title}</p>
                        <p className="text-[11px] opacity-70 leading-tight line-clamp-1 pointer-events-none">{event.client}</p>
                    </>
                )}
            </div>

            {/* Loading overlay */}
            {isLoading && (
                <div className="absolute inset-0 rounded flex items-center justify-center bg-white/60 z-20">
                    <Loader2 size={14} className="animate-spin opacity-80" />
                </div>
            )}
        </motion.div>
    );
};

export default DraggableEvent;
